/**
 * CAOS-294 — the light/dark THEME preference seam for the shell.
 *
 * The design system (design-system.ts) owns brand IDENTITY (accent, fonts, wordmark); this module
 * owns the light/dark MODE. The two are deliberately separate: the mode-variant colour tokens
 * (surfaces, neutrals, heat) live in the platform's own token set — `design-system/dist/caos-default.css`
 * in the workspace, mirrored by `@caos/ui`'s `tokens.css` for the library — keyed on `:root[data-theme=…]` and
 * `@media (prefers-color-scheme)`, so flipping `data-theme` on `<html>` re-skins every component with
 * zero per-component work — but ONLY if no installed theme stamps those same tokens inline (inline
 * wins over the stylesheet). `applyDesignSystem` therefore withholds the mode-variant tokens from the
 * inline stamp, leaving light/dark to the cascade this module drives.
 *
 * Preference model (mirrors the original Dragon OS header toggle): three states cycled by the topbar
 * theme button — `system` (honour the OS `prefers-color-scheme`, no `data-theme`), `light`, `dark`.
 * The choice is a person preference (CAOS-470): the kernel keeps it, so it follows the person to any
 * browser, and until they sign in and their preferences load the theme follows the operating system.
 */

import { preference, setPreference, PREFERENCES_CHANGE_EVENT } from './person-preferences.js';

/** The user's theme preference. `system` defers to the OS `prefers-color-scheme`. */
export type ThemePref = 'system' | 'light' | 'dark';

/** The document event the shell + Profile listen on to keep every theme control in sync. */
export const THEME_CHANGE_EVENT = 'caos-theme-change';

/**
 * Stamped on `<html>` as `data-brand` when nothing else has set one. The VALUE selects nothing:
 * since CAOS-172 the platform's own generation IS the unscoped base in both token files, so there
 * is no `[data-brand="caos"]` rule for it to match, and nothing anywhere compares against it.
 *
 * What is load-bearing is that the attribute EXISTS. `<caos-topbar>` re-reads its brand lockup only
 * on a `data-brand` mutation, so both `applyDesignSystem` (design-system.ts) and the theme editor's
 * save path re-set the attribute to its own current value to trigger that observer — and those
 * re-touches are guarded on the attribute being present. Remove this stamp and a fresh document
 * has no `data-brand` at all, so the nudge never fires and the lockup stops re-syncing. A
 * deployment that lays a customer override on top replaces this value rather than adding to it.
 */
const BRAND = 'caos';

const isPref = (v: unknown): v is ThemePref => v === 'system' || v === 'light' || v === 'dark';

/** Read the persisted preference, defaulting to `system` (respect the OS setting until told otherwise). */
export function currentThemePref(): ThemePref {
  try {
    const stored = preference('theme');
    if (isPref(stored)) return stored;
  } catch {
    /* localStorage may be unavailable (privacy mode / tests) — fall back to system. */
  }
  return 'system';
}

/**
 * Persist the preference (best-effort; a storage failure never blocks applying the theme). Applying
 * what is already in force writes nothing — including `system` when nothing is stored, so showing
 * the default after a reset does not record it as a choice.
 */
function persistThemePref(pref: ThemePref): void {
  try {
    if ((preference('theme') ?? 'system') !== pref) void setPreference('theme', pref);
  } catch {
    /* ignore — the theme still applies for this session. */
  }
}

/**
 * Apply a preference to the document root: `light`/`dark` stamp `data-theme` so the explicit choice
 * wins over `prefers-color-scheme`; `system` REMOVES `data-theme` so the OS media query decides. Also
 * persists the choice. Returns the applied preference for convenience.
 */
export function applyThemePref(pref: ThemePref, root: HTMLElement = document.documentElement): ThemePref {
  if (pref === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', pref);
  persistThemePref(pref);
  // Broadcast so every theme control (the topbar button, the Profile preference) reflects the new
  // state, wherever the change originated. Guarded for a non-DOM test environment.
  try {
    document.dispatchEvent(new CustomEvent<ThemePref>(THEME_CHANGE_EVENT, { detail: pref }));
  } catch {
    /* no document (tests) — the preference still applied. */
  }
  return pref;
}

/** The next preference in the cycle the topbar button walks: system → light → dark → system. */
export function nextThemePref(pref: ThemePref): ThemePref {
  return pref === 'system' ? 'light' : pref === 'light' ? 'dark' : 'system';
}

/**
 * Initialise theming at boot, BEFORE first paint: activate the CAOS brand palette (the one switch
 * tokens.css documents) and apply the persisted preference. Safe to call more than once. Returns the
 * active preference so the caller can seed the topbar's `theme` attribute.
 */
export function initTheme(root: HTMLElement = document.documentElement): ThemePref {
  if (!root.hasAttribute('data-brand')) root.setAttribute('data-brand', BRAND);
  const pref = currentThemePref();
  applyThemePref(pref, root);
  // CAOS-470 — when the signed-in person's preferences load, show their theme. Applying a value the
  // cache already holds writes nothing back.
  document.addEventListener(PREFERENCES_CHANGE_EVENT, (ev) => {
    const keys = (ev as CustomEvent<{ keys?: string[] }>).detail?.keys ?? [];
    if (keys.includes('theme')) applyThemePref(currentThemePref(), root);
  });
  return pref;
}
